/**
 * Responsibility: ask what is actually owned before adding it.
 *
 * Adding straight from a row guessed the grade, and the guess was the card's
 * best one - so a raw copy bought for eight dollars joined the collection
 * valued as a PSA 10. The grade is not a detail of the card, it is most of
 * what the holding is worth: on a 2009 Curry autograph the ungraded price is
 * $690 and the PSA 10 is $22,465.
 *
 * Only grades the catalogue has a price for are offered, and each option
 * shows that price, so the choice is made against the number it will put in
 * the total rather than against a label.
 *
 * Cost is optional and stays empty by default. A collection is worth tracking
 * before every receipt has been found, and a zero would report the whole
 * value as profit.
 */
import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { AddHolding, MarketResult } from '../../api/client';
import { useTags } from '../../api/hooks/useTags';
import { money } from '../grading/money';
import { GRADING_COMPANIES } from './gradingColours';
import { TagCheckList } from './TagCheckList';

type AddHoldingDialogProps = {
  card: MarketResult | null;
  busy: boolean;
  onClose: () => void;
  /**
   * `tagIds` are the sub-collections ticked here.
   *
   * Passed out rather than written here because the holding does not exist
   * yet - there is no id to attach a grouping to until the add has come
   * back. The caller chains the two.
   */
  onAdd: (body: AddHolding, tagIds: string[]) => void;
};

export function AddHoldingDialog({ card, busy, onClose, onAdd }: AddHoldingDialogProps) {
  // Defaults to ungraded: it is the commonest thing to own, and the one a
  // wrong guess overvalues most.
  const [grade, setGrade] = useState('Ungraded');
  // Kept apart from the grade label because the label comes from the
  // catalogue's own wording and the company drives the tile border.
  const [company, setCompany] = useState<string>('');
  const [quantity, setQuantity] = useState('1');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const tags = useTags();

  if (!card) return null;

  const priced = card.grades.filter((entry) => entry.price !== null);
  const options = priced.length > 0 ? priced : card.grades;
  const chosen = card.grades.find((entry) => entry.label === grade);
  // "Ungraded" is the catalogue's word for a raw copy; anything else is a
  // slab and needs to say who graded it.
  const raw = grade.toLowerCase() === 'ungraded';

  const submit = () => {
    const count = Number(quantity);
    onAdd({
      product_key: card.key,
      grade_label: grade,
      grading_company: raw ? null : company || 'PSA',
      quantity: Number.isFinite(count) && count >= 1 ? Math.floor(count) : 1,
      cost_basis: cost.trim() === '' ? null : cost.trim(),
      notes: notes.trim(),
    }, tagIds);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add to my collection</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {card.name}
          </Typography>

          <TextField
            select
            size="small"
            label="What do you own?"
            value={grade}
            onChange={(event) => setGrade(event.target.value)}
            helperText="Raw or slabbed, and which grade. This sets what the holding is worth."
          >
            {options.map((entry) => (
              <MenuItem key={entry.label} value={entry.label}>
                {entry.label}
                {entry.price !== null ? ` — ${money(entry.price)}` : ' — no price'}
              </MenuItem>
            ))}
          </TextField>

          {!raw ? (
            <TextField
              select
              size="small"
              label="Graded by"
              value={company || 'PSA'}
              onChange={(event) => setCompany(event.target.value)}
              helperText="Sets the border colour on the collection tile."
            >
              {GRADING_COMPANIES.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              label="How many"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value.replace(/[^0-9]/g, ''))}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              label="Paid each"
              placeholder="optional"
              value={cost}
              onChange={(event) => setCost(event.target.value.replace(/[^0-9.]/g, ''))}
              sx={{ flex: 1 }}
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
              }}
            />
          </Stack>

          <TextField
            size="small"
            label="Notes"
            placeholder="optional"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
          />

          <TagCheckList
            tags={tags.data ?? []}
            value={tagIds}
            onChange={setTagIds}
            empty="No sub-collections yet. Make one from the collection page to group this with others."
          />

          <Typography variant="caption" color="text.secondary">
            {chosen?.price
              ? `Worth ${money(chosen.price)} each at this grade.`
              : 'No recorded price at this grade, so it will count as unvalued until one appears.'}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={submit} disabled={busy}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
